/** How an account-level value is spelled on screen. One place, so two screens never disagree. */

import type { FeedState } from "@nebulize/sdk";

import type { StatusTone } from "@/components/ui";

/** The one axis a plan is priced on. Spaces are capped and are not a line on a bill. */
export const BILLED_QUOTAS: readonly string[] = ["concurrent_participants"];

/** The platform caps two things and the console shows exactly those two. */
const QUOTA_LABELS: Record<string, string> = {
  spaces: "Spaces",
  concurrent_participants: "Participants at once",
};

export function quotaLabel(name: string): string {
  return QUOTA_LABELS[name] ?? name;
}

const FEED_TONES: Record<FeedState, StatusTone> = {
  pending: "warning",
  authorized: "success",
  unreachable: "warning",
  revoked: "danger",
};

export function feedTone(state: FeedState | undefined): StatusTone {
  return state === undefined ? "neutral" : FEED_TONES[state];
}

const ROLE_LABELS: Record<string, string> = {
  account_owner: "Account owner",
  platform_admin: "Platform admin",
};

export function roleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role;
}

/** An absolute stamp, because "3 days ago" is not something to act on at speed. */
export function stamp(value: string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "never";
  const when = new Date(value);
  if (Number.isNaN(when.getTime())) return value;
  return when.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export function dayStamp(value: string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "never";
  const when = new Date(value);
  if (Number.isNaN(when.getTime())) return value;
  return when.toLocaleDateString(undefined, { dateStyle: "medium" });
}

export function clockStamp(at: number): string {
  return new Date(at).toLocaleTimeString(undefined, { timeStyle: "short" });
}

/** A scope reads as its kind, or as the kind and the one key naming which instance. */
export function scopeLabel(scope: string, key: string | null | undefined): string {
  return key === null || key === undefined || key === "" ? scope : `${scope} / ${key}`;
}

export interface SourceTally {
  kind: string;
  plan_id: string;
  count: number;
}

/** An account can hold hundreds of grant rows naming one plan. Collapse to the distinct terms. */
export function tallySources(
  sources: readonly { kind: string; plan_id: string }[],
): SourceTally[] {
  const held = new Map<string, SourceTally>();
  for (const source of sources) {
    const key = `${source.kind}:${source.plan_id}`;
    const found = held.get(key);
    if (found === undefined) held.set(key, { ...source, count: 1 });
    else found.count += 1;
  }
  return [...held.values()];
}

export function errorText(error: unknown): string {
  return error instanceof Error ? error.message : "Read failed";
}
